import React, { useState, useEffect } from 'react';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Loader2 } from 'lucide-react';

const Login = () => {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // If the auth gate bounced a signed-in account back here (e.g. no access role
    // assigned), surface the reason instead of looking like sign-in silently failed.
    useEffect(() => {
        try {
            const reason = sessionStorage.getItem('authDeniedReason');
            if (reason) { setError(reason); sessionStorage.removeItem('authDeniedReason'); }
        } catch { /* ignore */ }
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please enter both email and password.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const auth = getAuth();
            // Staff sign in with their MOBILE NUMBER (+ 6-digit PIN). Their account
            // email is `${mobile}@support.local`, so convert a 10-digit input to it.
            // A real email (contains "@") is used as typed (for admin accounts).
            const raw = email.trim();
            const digits = raw.replace(/\D/g, '');
            const loginEmail = (!raw.includes('@') && digits.length === 10) ? `${digits}@support.local` : raw;
            const cred = await signInWithEmailAndPassword(auth, loginEmail, password);
            // Warehouse handover staff land on the scanning page, not the dashboard —
            // it is the only screen they need and the only one they can open.
            let landing = '/dashboard';
            try {
                const { getDatabase, ref, get } = await import('firebase/database');
                const snap = await get(ref(getDatabase(), `users/${cred.user.uid}/role`));
                const r = String(snap.val() || '').toLowerCase();
                if (r === 'dispatch' || r === 'handover') landing = '/handover';
                if (r === 'qc') landing = '/qc';
            } catch { /* fall back to the dashboard */ }
            navigate(landing);
        } catch (err) {
            console.error(err);
            let msg = 'Failed to sign in.';
            if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
                msg = 'Invalid email or password.';
            } else if (err.code === 'auth/too-many-requests') {
                msg = 'Too many failed attempts. Please try again later.';
            }
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-4">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">Welcome Back</h1>
                    <p className="text-slate-500">Sign in to access Team Daksha</p>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-6 text-sm flex items-center gap-2">
                        <span>⚠️</span> {error}
                    </div>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Mobile number or email</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <input
                                type="text"
                                inputMode="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                placeholder="10-digit mobile number"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin" size={20} />
                                Signing in...
                            </>
                        ) : (
                            'Sign In'
                        )}
                    </button>
                </form>

                <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                    <p className="text-xs text-slate-300">
                        Secure Agent Portal
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
